import React, { useEffect, useMemo, useRef, useState } from "react";
import CollapsingOptionPicker from "./components/CollapsingOptionPicker";
import CustomerCard, { CustomerFields } from "./components/CustomerCard";
import LineItemCard from "./components/LineItemCard";
import StickyTotalBar from "./components/StickyTotalBar";
import {
  BENCH_TYPES,
  CONSTRUCTION_TYPES,
  addItemOptionsForRow,
  buildAddItemCategories,
  computeEstimate,
  filterConstructionOptions,
  filterDeckingOptions,
  filterDemoOptions,
  filterFastenerOptions,
  filterRailingOptions,
  filterSkirtingOnlyOptions,
  excludeHiddenMainStairs,
  filterStairsOptions,
  isConstructionTypeCategory,
  normalizeCat,
  type AddItemRow,
  type PricingCategoryRow,
  type PricingItemRow,
} from "../shared/pricing";
import {
  emptyDraft,
  loadCloudDraft,
  saveCloudDraft,
  fetchLivePricing,
  snapshotToEstimateInput,
  makeDebouncer,
  type DraftSnapshot,
} from "../shared/draft";
import {
  defaultFileName,
  textToBase64,
} from "../shared/estimateFile";
import {
  invokeSendProposalEmail,
  saveProposalAndGetLink,
} from "../shared/proposalSend";
import {
  getDropboxFileFor,
  getDropboxStatus,
  rememberDropboxFile,
  saveEstimateToDropbox,
} from "../shared/dropboxClient";
import {
  findMissingSections,
  missingSignature,
  type MissingSection,
} from "../shared/completeness";
import { supabase } from "../supabaseClient";
import { uid } from "../utils/uid";
import { dlog } from "../debugLog";
import { snapshotDateForSave } from "../shared/pricesAsOf";

/** Placeholder categories that render AFTER Decking + Railing.
 *  Construction Type is rendered separately at the top of the
 *  stack (above Decking) — per the original product spec. */
const PLACEHOLDER_AFTER: string[] = [];

/**
 * EstimatorMobile (R2b)
 * - Holds a full DraftSnapshot in state so non-edited fields round-trip.
 * - Bootstraps by loading the user's cloud draft. If none, fetches live
 *   pricing and starts with emptyDraft().
 * - Customer card + Decking + Railing cards write back into the snapshot.
 * - StickyTotalBar shows the live finalEstimate via computeEstimate.
 * - Debounced cloud upsert on every snapshot change.
 *
 * The other 5 line-item categories stay placeholders this round; R2c
 * wires them.
 *
 * orgId / userSettings come from MobileApp's shell bootstrap and are
 * unused in this round — Round 2 will read them when wiring Review &
 * Send (orgId for org_proposal_sections, userSettings for replyTo +
 * email subject/body templates).
 */
type EstimatorMobileProps = {
  orgId?: string | null;
  userId?: string | null;
  userSettings?: any;
  /** Allow-listed admin → show the Zoho import entry on the Customer card. */
  canImport?: boolean;
  /** Live connectivity — Zoho import is online-only. */
  isOnline?: boolean;
  /** Round 2: fires when the user taps "Review & Send". MobileApp uses
   *  this to navigate to MobileProposalPage with the current snapshot.
   *  Receives the live snapshot + pricing + computed estimate so the
   *  proposal screen sees what the user is looking at now (no race
   *  against the 800ms debounced cloud upsert). */
  onRequestReview?: (payload: {
    snapshot: DraftSnapshot;
    pricingItems: PricingItemRow[];
    pricingCategories: PricingCategoryRow[];
    estimate: ReturnType<typeof computeEstimate>;
  }) => void;
  /** Bumped by MobileApp each time the user taps "New" (header "+ New" or
   *  the Home card). Any value above handledNewEstimateNonce is a tap still
   *  waiting to be applied — including one made before this screen mounted
   *  (Home card) or while the draft was still loading. */
  newEstimateNonce?: number;
  /** The last tap already applied. Lives in MobileApp so it survives this
   *  screen unmounting when the user switches tabs. Omitted → only taps made
   *  after mount count (the old behaviour). */
  handledNewEstimateNonce?: number;
  /** Called with the tap's nonce as soon as it is applied (confirmed,
   *  cancelled or not needed), so it never runs twice. */
  onNewEstimateHandled?: (nonce: number) => void;
  /** Proposal-screen edits (SOW / Project Description) pushed back on return
   *  from the proposal, so this snapshot — the source of truth + cloud writer —
   *  stays in sync. Applied once per nonce bump. */
  externalPatch?: { patch: Partial<DraftSnapshot>; nonce: number } | null;
  /** An estimate file the user opened from the Files picker (iOS → Dropbox).
   *  MobileApp reads and validates the file; this applies it. Applied once per
   *  nonce bump, same one-shot contract as externalPatch. */
  openedFile?: {
    snapshot: any;
    fileName: string;
    nonce: number;
    /** Came from Dropbox: Save to Dropbox writes back to this file. */
    dropboxPath?: string;
  } | null;
};

export default function EstimatorMobile(props: EstimatorMobileProps = {}) {
  const [snapshot, setSnapshot] = useState<DraftSnapshot>(() => emptyDraft());
  const [pricingItems, setPricingItems] = useState<PricingItemRow[]>([]);
  const [pricingCategories, setPricingCategories] = useState<
    PricingCategoryRow[]
  >([]);
  const [userId, setUserId] = useState<string>("");
  const [bootstrapped, setBootstrapped] = useState(false);

  // Whether the CURRENT estimate has been exported (Export Via Email) since
  // its last edit. Drives the "New" guard: an estimate with data that hasn't
  // been exported prompts a confirm before it's cleared. Reset to false on any
  // field edit (see patchSnapshot) and when a new estimate starts; set true
  // when an export succeeds.
  const [exported, setExported] = useState(false);

  // -----------------------------------------------------------------
  // Mount bootstrap: session → cloud draft → fallback to live pricing
  // -----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionRes } = await supabase.auth.getSession();
      if (cancelled) return;
      const uidFromSession = sessionRes?.session?.user?.id || "";
      dlog("[draft] mobile bootstrap — session user", {
        userId: uidFromSession,
        hasSession: !!sessionRes?.session,
      });
      setUserId(uidFromSession);

      let snap: DraftSnapshot | null = null;
      if (uidFromSession) {
        snap = await loadCloudDraft(supabase, uidFromSession);
        if (cancelled) return;
      }

      if (snap) {
        dlog("[draft] mobile bootstrap — applying cloud snapshot", {
          estimateId: snap.estimateId,
          clientLastName: snap.clientLastName,
        });
        setSnapshot(snap);
        if (
          Array.isArray(snap.pricingItems) &&
          snap.pricingItems.length > 0
        ) {
          // Use the snapshot's embedded (frozen) pricing for parity with
          // whichever app saved this draft.
          setPricingItems(snap.pricingItems);
          if (Array.isArray(snap.pricingCategories)) {
            setPricingCategories(snap.pricingCategories);
          }
          setBootstrapped(true);
          return;
        }
      } else {
        dlog("[draft] mobile bootstrap — no cloud snapshot, going live");
      }

      // No cloud snapshot, or snapshot had no embedded pricing — go live.
      const live = await fetchLivePricing(supabase);
      if (cancelled) return;
      if (live) {
        dlog("[draft] mobile bootstrap — live pricing loaded", {
          items: live.items.length,
          categories: live.categories.length,
        });
        setPricingItems(live.items);
        setPricingCategories(live.categories);
      }
      setBootstrapped(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // -----------------------------------------------------------------
  // Debounced cloud upsert on snapshot changes. The 800ms window
  // coalesces rapid customer-info keystrokes into one Supabase write.
  // -----------------------------------------------------------------
  const saverRef = useRef(
    makeDebouncer((id: string, snap: DraftSnapshot) => {
      void saveCloudDraft(supabase, id, snap);
    }, 800)
  );

  useEffect(() => {
    if (!bootstrapped) return;
    if (!userId) return;
    // Don't write a ghost row before the user has touched anything.
    // patchSnapshot stamps a real estimateId on the first edit, so an
    // empty estimateId == "user hasn't touched the estimator yet".
    if (!snapshot.estimateId) {
      dlog("[draft] mobile save skipped — no user edits yet");
      return;
    }
    dlog("[draft] mobile save scheduled (debouncer)", {
      estimateId: snapshot.estimateId,
      clientLastName: snapshot.clientLastName,
      selectedDeckingId: snapshot.selectedDeckingId,
      deckingSqFt: snapshot.deckingSqFt,
    });
    saverRef.current(userId, snapshot);
  }, [snapshot, bootstrapped, userId]);

  // Flush pending save when the user leaves the page or backgrounds the
  // tab. iOS Safari is unreliable about firing beforeunload, so we also
  // listen for pagehide and visibilitychange — any of these means edits
  // might be lost if we don't flush now.
  useEffect(() => {
    const flush = () => saverRef.current.flush();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // -----------------------------------------------------------------
  // Skirting auto-prefill from Decking selection.
  //
  // When the user picks a Decking product, pre-select the matching
  // Skirting product (exact name first, else most-overlapping-name).
  // Never overwrites a manually chosen Skirting — once the user
  // touches the Skirting controls, the prefill stops cold for the
  // session. Lattice is never touched by this flow.
  // -----------------------------------------------------------------
  const skirtingUserTouchedRef = useRef(false);
  const lastPrefillFromDeckingRef = useRef<string>("");

  // If the cloud snapshot already has a Skirting selection at bootstrap,
  // treat it as user-set so we don't overwrite it on first Decking pick.
  useEffect(() => {
    if (!bootstrapped) return;
    if (snapshot.selectedSkirtingId) {
      skirtingUserTouchedRef.current = true;
    }
    // Only on bootstrap transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapped]);

  useEffect(() => {
    if (!bootstrapped) return;
    if (skirtingUserTouchedRef.current) return;

    const deckingId = String(snapshot.selectedDeckingId || "");
    if (!deckingId) return;
    if (deckingId === lastPrefillFromDeckingRef.current) return;

    const deckingItem = pricingItems.find(
      (p) => String(p.id) === deckingId
    );
    if (!deckingItem) return;

    const skirtingOpts = filterSkirtingOnlyOptions(pricingItems);
    if (skirtingOpts.length === 0) return;

    const tokenize = (s: string) =>
      s.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length > 1);
    const deckTokens = new Set(tokenize(deckingItem.name || ""));
    const deckNameLc = (deckingItem.name || "").toLowerCase();

    // Exact name match first, else most overlapping tokens, falling back
    // to the first option so the user is never left blank.
    let match = skirtingOpts.find(
      (s) => (s.name || "").toLowerCase() === deckNameLc
    );
    if (!match) {
      let bestScore = -1;
      for (const s of skirtingOpts) {
        const sTokens = tokenize(s.name || "");
        const score = sTokens.reduce(
          (n, t) => n + (deckTokens.has(t) ? 1 : 0),
          0
        );
        if (score > bestScore) {
          bestScore = score;
          match = s;
        }
      }
    }
    if (!match) return;

    lastPrefillFromDeckingRef.current = deckingId;
    patchSnapshot({
      skirtingCategory: "Skirting",
      selectedSkirtingId: String(match.id),
    });
    // patchSnapshot is stable enough for this effect's purpose; depending on
    // it would re-fire on every snapshot change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.selectedDeckingId, bootstrapped, pricingItems]);

  // -----------------------------------------------------------------
  // Live estimate. computeEstimate is the same function desktop uses.
  // Same inputs → same numbers, to the dollar.
  // -----------------------------------------------------------------
  const estimate = useMemo(
    () => computeEstimate(snapshotToEstimateInput(snapshot, pricingItems)),
    [snapshot, pricingItems]
  );

  // -----------------------------------------------------------------
  // Uplift breakdown — same rounded display values desktop renders
  // in its right-rail uplift-hover-box (App.tsx ~3041). Pure local
  // computation from computeEstimate scalars; no pricing math.
  // -----------------------------------------------------------------
  const upliftBreakdown = useMemo(() => {
    const basePrice =
      (Number(estimate.deckingSubtotal) || 0) +
      (Number(estimate.railingSubtotal) || 0) +
      (Number(estimate.stairsSubtotal) || 0) +
      (Number(estimate.fastenerSubtotal) || 0) +
      (Number(estimate.demoSubtotal) || 0) +
      (Number(estimate.skirtingSubtotal) || 0) +
      estimate.addItemsDetailed
        .filter((r) => r && r.picked && Number(r.lineBase || 0) !== 0)
        .reduce((sum, r) => sum + (Number(r.lineBase) || 0), 0);

    const permitAmt = (basePrice * (Number(estimate.permitPercent) || 0)) / 100;
    const smallJobAmt =
      (basePrice * (Number(estimate.smallJobPercent) || 0)) / 100;
    const msrp = basePrice + permitAmt + smallJobAmt;

    const perceivedAmt =
      (basePrice * (Number(estimate.perceivedPercent) || 0)) / 100;
    const financeAmt =
      (basePrice * (Number(estimate.financePercent) || 0)) / 100;
    const miAmt = (basePrice * (Number(estimate.miPercent) || 0)) / 100;
    const upliftSubtotal = perceivedAmt + financeAmt + miAmt;
    const totalCost = msrp + perceivedAmt + financeAmt + miAmt;

    const r0 = (n: number) => Math.round(Number(n) || 0);
    return {
      basePrice: r0(basePrice),
      permitAmt: r0(permitAmt),
      smallJobAmt: r0(smallJobAmt),
      msrp: r0(msrp),
      perceivedAmt: r0(perceivedAmt),
      financeAmt: r0(financeAmt),
      miAmt: r0(miAmt),
      upliftSubtotal: r0(upliftSubtotal),
      totalCost: r0(totalCost),
    };
  }, [estimate]);

  const money0 = (n: number) =>
    (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  // "×1.17" or "×1.2" — one decimal when the second decimal is 0.
  const formatMultiplier = (m: number) => {
    const safe = Number(m) || 0;
    const two = safe.toFixed(2);
    return "×" + (two.endsWith("0") ? safe.toFixed(1) : two);
  };

  const [upliftOpen, setUpliftOpen] = useState(false);

  // Categories shown in the Add Item card's category dropdown. Same
  // builder desktop uses, so the option list matches.
  const addItemCategories = useMemo(
    () => buildAddItemCategories(pricingCategories),
    [pricingCategories]
  );

  // Line-item dropdown options (same filters desktop uses).
  const constructionOptions = useMemo(
    () => filterConstructionOptions(pricingItems),
    [pricingItems]
  );
  const deckingOptions = useMemo(
    () => filterDeckingOptions(pricingItems),
    [pricingItems]
  );
  const railingOptions = useMemo(
    () => filterRailingOptions(pricingItems),
    [pricingItems]
  );
  const stairsOptions = useMemo(
    () => filterStairsOptions(pricingItems),
    [pricingItems]
  );
  // Main Stairs picker hides the add-on-only rows (Platform stairs, Mitered
  // Treads, Curved Step); they stay available as "Stair options" +Add Items.
  const mainStairsOptions = useMemo(
    () => excludeHiddenMainStairs(stairsOptions),
    [stairsOptions]
  );
  const demoOptions = useMemo(
    () => filterDemoOptions(pricingItems),
    [pricingItems]
  );
  const fastenerOptions = useMemo(
    () => filterFastenerOptions(pricingItems),
    [pricingItems]
  );

  // -----------------------------------------------------------------
  // Field setters — each patches the snapshot. Touching any field
  // also stamps estimateId/savedAt so a brand-new draft becomes
  // "real" the moment the user types anything.
  // -----------------------------------------------------------------
  const patchSnapshot = (patch: Partial<DraftSnapshot>) => {
    // Any edit invalidates a prior export — the exported copy is now stale.
    // (No-op re-render when already false; React bails on an unchanged value.)
    setExported(false);
    setSnapshot((prev) => ({
      ...prev,
      ...patch,
      estimateId: prev.estimateId || uid(),
      savedAt: new Date().toISOString(),
    }));
  };

  const customer: CustomerFields = {
    clientFirstName: snapshot.clientFirstName || "",
    clientLastName: snapshot.clientLastName || "",
    clientLocation: snapshot.clientLocation || "",
    clientTown: snapshot.clientTown || "",
    clientState: snapshot.clientState || "",
    clientZip: snapshot.clientZip || "",
    clientPhone: snapshot.clientPhone || "",
    clientEmail: snapshot.clientEmail || "",
  };

  const onCustomerChange = (next: CustomerFields) => patchSnapshot(next);

  // -----------------------------------------------------------------
  // Add-item helpers — same AddItemRow shape desktop's
  // addAddItemRow / updateAddItemRow / removeAddItemRow use, so the
  // snapshot round-trips cross-device. Mobile appends new rows to the
  // bottom of the visual stack (instructions); desktop prepends in
  // its own UI but iterates the same array.
  // -----------------------------------------------------------------
  const addItems: AddItemRow[] = Array.isArray(snapshot.addItems)
    ? snapshot.addItems
    : [];

  const addAddItemRow = () => {
    const newRow: AddItemRow = {
      rowId: uid(),
      category: "",
      itemId: "",
      qty: 0,
      constructionType: "",
      deckingId: "",
      customName: "",
      customPrice: 0,
    };
    patchSnapshot({ addItems: [...addItems, newRow] });
  };

  const updateAddItemRow = (rowId: string, patch: Partial<AddItemRow>) => {
    patchSnapshot({
      addItems: addItems.map((r) =>
        r.rowId === rowId ? { ...r, ...patch } : r
      ),
    });
  };

  const removeAddItemRow = (rowId: string) => {
    patchSnapshot({ addItems: addItems.filter((r) => r.rowId !== rowId) });
  };

  // -----------------------------------------------------------------
  // Review & Send: enable when customer ready AND at least one of
  // Decking / Railing is configured. Tapping just logs in R2b.
  // -----------------------------------------------------------------
  const customerReady =
    !!customer.clientLastName.trim() &&
    !!customer.clientTown.trim() &&
    !!customer.clientEmail.trim();

  const constructionConfigured = !!snapshot.constructionType;
  const deckingConfigured =
    !!snapshot.selectedDeckingId && Number(snapshot.deckingSqFt) > 0;
  const railingConfigured =
    !!snapshot.selectedRailingId && Number(snapshot.railingLf) > 0;
  const stairsConfigured =
    !!snapshot.selectedStairsId && Number(snapshot.stairsCount) > 0;
  const demoConfigured =
    !!snapshot.selectedDemoId && Number(snapshot.demoQty) > 0;
  // Fasteners qty is auto-derived from deckingSqFt, so "configured"
  // means a fastener type has been chosen.
  const fastenerConfigured = !!snapshot.selectedFastenerId;
  const skirtConfigured =
    !!snapshot.selectedSkirtingId && Number(snapshot.skirtingSf) > 0;
  const addItemsHaveValue =
    (Number(estimate.addItemsSubtotalUpliftable) || 0) +
      (Number(estimate.addItemsSubtotalFixed) || 0) >
    0;
  const anyLineItem =
    deckingConfigured ||
    railingConfigured ||
    stairsConfigured ||
    demoConfigured ||
    fastenerConfigured ||
    skirtConfigured ||
    addItemsHaveValue;
  const canSubmit = customerReady && anyLineItem;

  // Active-step indicator: a single blue outline that walks down the card
  // stack so the user always sees "what to do next". Advances in two ways
  // so the user is never stuck on a card they want to skip:
  //   1. AUTO — when the previous card becomes configured, the highlight
  //      moves to the next un-configured card down the stack.
  //   2. TAP — when the user taps a card's head (open or close), the
  //      highlight follows their attention. Forward-only via a ratchet
  //      (max of auto + user index) so the highlight never snaps backward
  //      when the user goes back to edit something earlier.
  // Purely visual — no programmatic focus or scroll, since iOS handles
  // those poorly when the keyboard is up. Tap is a real user gesture, so
  // the effect is safe.
  const CARD_KEYS = useMemo(
    () =>
      [
        "customer",
        "construction",
        "decking",
        "railing",
        "stairs",
        "demo",
        "fastener",
        "skirt",
      ] as const,
    []
  );
  type CardKey = (typeof CARD_KEYS)[number];

  const autoStepIndex = !customerReady
    ? 0
    : !constructionConfigured
    ? 1
    : !deckingConfigured
    ? 2
    : !railingConfigured
    ? 3
    : !stairsConfigured
    ? 4
    : !demoConfigured
    ? 5
    : !fastenerConfigured
    ? 6
    : !skirtConfigured
    ? 7
    : CARD_KEYS.length;

  const [userActiveIndex, setUserActiveIndex] = useState(0);
  const effectiveStepIndex = Math.max(autoStepIndex, userActiveIndex);
  const activeStep: CardKey | null =
    effectiveStepIndex < CARD_KEYS.length
      ? CARD_KEYS[effectiveStepIndex]
      : null;

  const onCardTap = (key: CardKey) => {
    const idx = CARD_KEYS.indexOf(key);
    if (idx < 0) return;
    setUserActiveIndex((prev) => (idx > prev ? idx : prev));
  };

  // -----------------------------------------------------------------
  // Enter advances to the next field.
  //
  // Every numeric field already carries inputMode="decimal", so iOS
  // shows the number pad — but the return key said "return" and simply
  // dismissed the keyboard. Entering a deck was therefore
  // tap-scroll-tap per quantity: eight keyboard teardowns to fill in
  // eight numbers.
  //
  // Done by delegation on .m-content rather than props on each input,
  // for two reasons: it picks up the Customer card's fields (a separate
  // component) with no prop drilling, and the chain stays correct as
  // LineItemCards mount and unmount their bodies — the field order is
  // read from the live DOM at the moment Enter is pressed, not baked in
  // at render.
  // -----------------------------------------------------------------
  const contentRef = useRef<HTMLElement | null>(null);

  /** Focusable text/number inputs inside the estimate, in DOM order.
   *  Checkboxes/radios/buttons are not typed into, and a collapsed
   *  card's inputs are unmounted (LineItemCard renders its body only
   *  when open), so offsetParent filters what is merely hidden. */
  const orderedFields = (root: HTMLElement): HTMLInputElement[] =>
    Array.from(
      root.querySelectorAll<HTMLInputElement>(
        'input:not([type="checkbox"]):not([type="radio"]):not([type="button"])' +
          ":not([disabled]):not([readonly])"
      )
    ).filter((el) => el.offsetParent !== null);

  const handleContentKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Enter") return;
    const target = e.target as HTMLElement;
    // Textareas keep Enter as a newline — SOW/Notes are multi-line.
    if (target.tagName !== "INPUT") return;
    const root = contentRef.current;
    if (!root) return;
    const fields = orderedFields(root);
    const i = fields.indexOf(target as HTMLInputElement);
    if (i < 0) return;
    // Stop the implicit-submit / keyboard-bounce the browser would do.
    e.preventDefault();
    const next = fields[i + 1];
    if (next) {
      next.focus();
      // Land with the old value selected so typing overwrites it —
      // these are quantities being replaced, not appended to. select()
      // throws on input types that don't support selection, hence try.
      try {
        next.select();
      } catch {
        /* type doesn't support selection — focus alone is fine */
      }
    } else {
      // Last field: dismiss the keyboard rather than trapping focus.
      (target as HTMLInputElement).blur();
    }
  };

  // Label the return key per field: "next" everywhere, "done" on the
  // last one, so the keyboard tells the truth about what Enter will do.
  // Intentionally no dependency array — cards open and close and rows are
  // added, and any of those changes both the field set and which field is
  // last. It is one querySelectorAll over a small subtree.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const fields = orderedFields(root);
    fields.forEach((el, i) => {
      el.setAttribute(
        "enterkeyhint",
        i === fields.length - 1 ? "done" : "next"
      );
    });
  });

  // -----------------------------------------------------------------
  // "New" estimate — clear all fields/state and start blank, then wipe the
  // persisted cloud draft so reopening the app doesn't restore the old one.
  // Guarded: if the current estimate has data that hasn't been exported,
  // confirm first. Mobile has no "save" — the only output is Export Via
  // Email — so the warning references export, not save.
  // -----------------------------------------------------------------
  const startNewEstimate = () => {
    // Drop any pending debounced save of the OLD snapshot so it can't land
    // after we clear, then blank the cloud draft explicitly (emptyDraft has no
    // estimateId, so the change-effect intentionally won't persist it for us).
    saverRef.current.cancel();
    const blank = emptyDraft();
    setSnapshot(blank);
    setUserActiveIndex(0);
    setExported(false);
    setEmailStatus(null);
    skirtingUserTouchedRef.current = false;
    lastPrefillFromDeckingRef.current = "";
    // Fresh estimate → drop the "I know these are blank" acknowledgement.
    ackedMissingRef.current = "";
    if (userId) {
      void saveCloudDraft(supabase, userId, blank);
    }
  };

  const requestNewEstimate = () => {
    const hasData = !!snapshot.estimateId; // estimateId is stamped on first edit
    if (hasData && !exported) {
      const ok = window.confirm(
        "This estimate hasn't been exported yet. Start a new one anyway?"
      );
      if (!ok) return; // keep the current estimate
    }
    startNewEstimate();
  };

  // React to MobileApp's "New" taps. A tap is applied once the estimate has
  // finished loading, never dropped: before, a tap from the Home card (made
  // while this screen wasn't mounted) or during the draft load was marked
  // handled and thrown away, and the load then put the old estimate back.
  // It always goes through requestNewEstimate, so an un-exported estimate
  // still asks before it is cleared.
  const newEstimateNonce = props.newEstimateNonce ?? 0;
  const mountNonceRef = useRef(newEstimateNonce);
  const handledNewNonce =
    props.handledNewEstimateNonce ?? mountNonceRef.current;
  // Local guard too, so the same tap can't run twice before MobileApp's
  // handled value comes back down as a prop.
  const appliedNewNonceRef = useRef(handledNewNonce);
  useEffect(() => {
    if (!bootstrapped) return; // keep the tap; apply it once loaded
    const target = newEstimateNonce;
    if (target <= handledNewNonce || target <= appliedNewNonceRef.current) {
      return;
    }
    appliedNewNonceRef.current = target;
    props.onNewEstimateHandled?.(target);
    requestNewEstimate();
    // requestNewEstimate closes over the live snapshot/exported; it must run
    // only for a new tap, not when those values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEstimateNonce, handledNewNonce, bootstrapped]);

  // Merge proposal-screen edits (SOW / Project Description) back into the
  // snapshot when returning from the proposal. Skips the initial value; only a
  // real nonce bump applies. Without this, the proposal's edits live only in
  // MobileApp's payload copy, so re-tapping "Review & Send" rebuilds from this
  // (stale) snapshot and wipes them — and a later estimator save would clobber
  // them in the cloud.
  const externalPatchNonce = props.externalPatch?.nonce ?? 0;
  const prevExternalNonceRef = useRef(externalPatchNonce);
  useEffect(() => {
    if (externalPatchNonce === prevExternalNonceRef.current) return;
    prevExternalNonceRef.current = externalPatchNonce;
    const patch = props.externalPatch?.patch;
    if (!patch) return;
    setSnapshot((prev) => ({
      ...prev,
      ...patch,
      savedAt: new Date().toISOString(),
    }));
    // Only react to a genuine nonce change, not to unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPatchNonce]);

  // -----------------------------------------------------------------
  // Apply a file the user opened from the Files picker (iOS → Dropbox).
  // MobileApp already read + validated it; this is the state handoff.
  //
  // Guarded by the same unexported-work confirm as "New", because opening a
  // file replaces the current estimate just as destructively — and on mobile
  // there's no Save to fall back on.
  // -----------------------------------------------------------------
  const openedFileNonce = props.openedFile?.nonce ?? 0;
  const prevOpenedNonceRef = useRef(openedFileNonce);
  useEffect(() => {
    if (openedFileNonce === prevOpenedNonceRef.current) return;
    prevOpenedNonceRef.current = openedFileNonce;
    if (!bootstrapped) return;

    const incoming = props.openedFile?.snapshot;
    if (!incoming) return;

    const hasData = !!snapshot.estimateId;
    if (hasData && !exported) {
      const ok = window.confirm(
        "This estimate hasn't been exported yet. Open the file anyway? " +
          "Your current estimate will be replaced."
      );
      if (!ok) return;
    }

    // Merge over emptyDraft() rather than trusting the file wholesale: a
    // legacy .DUest predates fields the mobile UI reads, and a missing key
    // would surface as an undefined crash deep in a card. Also stamp an
    // estimateId when the file has none (saved before the first edit), since
    // the autosave effect treats a blank id as "untouched" and would never
    // persist the opened estimate.
    const merged: DraftSnapshot = {
      ...emptyDraft(),
      ...incoming,
      estimateId: incoming.estimateId || uid(),
      savedAt: new Date().toISOString(),
    };

    // Drop any pending debounced write of the OUTGOING snapshot so it can't
    // land after this one and resurrect the replaced estimate.
    saverRef.current.cancel();
    setSnapshot(merged);
    setUserActiveIndex(0);
    setEmailStatus(null);
    skirtingUserTouchedRef.current = false;
    lastPrefillFromDeckingRef.current = "";

    // A file opened from the phone is unexported work — nothing has been
    // saved or emailed from this device yet. One opened from Dropbox is
    // already saved there, unchanged; remember the file so Save to Dropbox
    // goes back to it rather than making a copy in the folder setting.
    const fromDropbox = props.openedFile?.dropboxPath || "";
    if (fromDropbox) {
      rememberDropboxFile(merged.estimateId, fromDropbox);
      setDropboxFileTick((n) => n + 1);
    }
    setExported(!!fromDropbox);

    // Match the bootstrap rule: a snapshot carrying embedded (frozen) pricing
    // prices exactly as the app that saved it did. Without this an older file
    // would silently reprice against today's catalog.
    if (Array.isArray(merged.pricingItems) && merged.pricingItems.length > 0) {
      setPricingItems(merged.pricingItems);
      if (Array.isArray(merged.pricingCategories)) {
        setPricingCategories(merged.pricingCategories);
      }
    }

    if (userId) {
      void saveCloudDraft(supabase, userId, merged);
    }
    // Runs only on a genuine nonce change; the guard reads live snapshot/
    // exported deliberately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedFileNonce, bootstrapped]);

  // -----------------------------------------------------------------
  // "Did I forget something?" guard. Every scope category is optional on
  // its own, so an estimate with no railing used to sail straight through
  // to the proposal — which is exactly how one went out. Review & Send now
  // shows the blank / half-filled categories first and makes the user
  // acknowledge them. The acknowledgement is remembered per estimate + per
  // set of gaps, so a second tap doesn't nag, but changing the gaps asks
  // once more.
  // -----------------------------------------------------------------
  const [missingSections, setMissingSections] = useState<
    MissingSection[] | null
  >(null);
  const ackedMissingRef = useRef<string>("");

  // Priced rows, so an Add Items line that comes out to $0 is caught too.
  const currentMissing = () =>
    findMissingSections({ ...snapshot, addItems: estimate.addItemsDetailed });
  const currentMissingKey = () =>
    `${snapshot.estimateId}|${missingSignature(currentMissing())}`;

  const proceedToReview = () => {
    // Flush any debounced cloud save so the proposal screen +
    // anything that re-reads the cloud row sees the latest edits.
    saverRef.current.flush();
    if (props.onRequestReview) {
      props.onRequestReview({
        snapshot,
        pricingItems,
        pricingCategories,
        estimate,
      });
    }
  };

  const onReview = () => {
    const missing = currentMissing();
    if (missing.length > 0 && ackedMissingRef.current !== currentMissingKey()) {
      setMissingSections(missing);
      return;
    }
    proceedToReview();
  };

  /** "Continue anyway" — remember this exact set of gaps, then go. */
  const acknowledgeMissing = () => {
    ackedMissingRef.current = currentMissingKey();
    setMissingSections(null);
    proceedToReview();
  };

  // -----------------------------------------------------------------
  // "Email to myself": email the contractor (app user) the estimate as a
  // .DUest attachment + the proposal /review link, so they can open it on
  // a laptop and Save-as-PDF there. Replaces the old share-sheet "Save".
  //
  // - The .DUest body is byte-identical to a normally-saved file (same
  //   DraftSnapshot + embedded current pricing), base64-encoded for Resend.
  // - We ensure a proposals row exists (saveProposalAndGetLink) so the
  //   emailed /review/<id> link resolves. That row is keyed on estimateId,
  //   so a later real send to the client updates the SAME row (no dupes).
  // - Recipient is userSettings.userEmail. The button is disabled (with a
  //   hint) when that's blank — same pattern as "Send me a copy".
  // -----------------------------------------------------------------
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailStatus, setEmailStatus] = useState<
    { ok: boolean; text: string } | null
  >(null);

  const selfEmail = (props.userSettings?.userEmail || "").trim();
  const hasEstimate = !!snapshot.estimateId;
  const canEmailSelf = hasEstimate && !!selfEmail && !emailBusy;

  // Hint under the buttons — explains WHY the button is disabled, or shows
  // the last send result.
  const emailHint = emailStatus
    ? emailStatus.text
    : !selfEmail
      ? "Set your email in Settings to enable"
      : !hasEstimate
        ? "Add customer info to start an estimate first"
        : "Emails the estimate file + proposal link to you";

  // Subject: "Town — LastName" (e.g. "Valley Stream — Lawson"); falls back
  // to "Estimate — <date>" when both town and last name are blank.
  const buildSelfSubject = (): string => {
    const town = (snapshot.clientTown || "").trim();
    const last = (snapshot.clientLastName || "").trim();
    const jobId = [town, last].filter(Boolean).join(" — ");
    if (jobId) return jobId;
    const date = new Date().toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return `Estimate — ${date}`;
  };

  // -----------------------------------------------------------------
  // "Save to Dropbox": one tap, the .est file lands in THIS person's own
  // Dropbox (connected in Settings), in their chosen folder — by default
  // /<year> Potentials/<Town> <Last>/<Town>_<Last>.est. The dropbox edge
  // function builds the path. Shown only when this login is connected;
  // otherwise the email export stays the left button. Same file body as
  // the email attachment / desktop Save. Settings is a separate tab, so
  // this re-checks each time the Estimate tab mounts.
  // -----------------------------------------------------------------
  const [dropboxEnabled, setDropboxEnabled] = useState(false);
  const [dropboxConnectable, setDropboxConnectable] = useState(false);
  const [dropboxBusy, setDropboxBusy] = useState(false);
  // Bumped when this estimate's Dropbox file is recorded, to re-read it.
  const [dropboxFileTick, setDropboxFileTick] = useState(0);
  const dropboxFile = useMemo(
    () => getDropboxFileFor(snapshot.estimateId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [snapshot.estimateId, dropboxFileTick]
  );

  useEffect(() => {
    let cancelled = false;
    void getDropboxStatus(supabase).then((s) => {
      if (cancelled) return;
      setDropboxEnabled(s.available && s.connected);
      setDropboxConnectable(s.available && !s.connected);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const buildEstimateFileText = (): string =>
    JSON.stringify(
      {
        ...snapshot,
        pricingItems,
        pricingCategories,
        // A file opened with its own saved prices keeps their date.
        pricingSnapshotAt: snapshotDateForSave(
          Array.isArray(snapshot.pricingItems) && snapshot.pricingItems.length > 0,
          snapshot.pricingSnapshotAt
        ),
      },
      null,
      2
    );

  const onSaveDropbox = async () => {
    if (!hasEstimate || dropboxBusy) return;
    setDropboxBusy(true);
    setEmailStatus(null);
    try {
      const res = await saveEstimateToDropbox(supabase, {
        town: snapshot.clientTown,
        lastName: snapshot.clientLastName,
        estimateName: snapshot.estimateName,
        content: buildEstimateFileText(),
        // Opened from / already saved to Dropbox → back to that same file.
        path: dropboxFile || undefined,
      });
      if (!res.ok) {
        setEmailStatus({ ok: false, text: `Couldn’t save it: ${res.error}` });
        return;
      }
      setEmailStatus({ ok: true, text: `Saved to Dropbox: ${res.data.path}` });
      // Later saves of this estimate go to the same file, even if the
      // customer's name is edited.
      rememberDropboxFile(snapshot.estimateId, res.data.path);
      setDropboxFileTick((n) => n + 1);
      // Same as a file save: "New" won't warn until it's edited again.
      setExported(true);
    } finally {
      setDropboxBusy(false);
    }
  };

  const onEmailSelf = async () => {
    if (!canEmailSelf) return;
    setEmailBusy(true);
    setEmailStatus(null);
    try {
      // 1. .DUest file content — IDENTICAL serialization to the file save.
      const fileText = buildEstimateFileText();
      const filename = defaultFileName({
        town: snapshot.clientTown,
        lastName: snapshot.clientLastName,
        estimateName: snapshot.estimateName,
      });
      const fileBase64 = textToBase64(fileText);

      // 2. Ensure a proposals row exists → /review link.
      const { proposalId, reviewLink } = await saveProposalAndGetLink(
        supabase,
        {
          snapshot,
          pricingItems,
          estimate,
          userSettings: props.userSettings,
          orgId: props.orgId ?? null,
          // No override: this flow has no live Notes state, so it must fall
          // back to the persisted notes. Passing "" here used to blank the
          // Notes on an already-sent proposal the customer was reading.
        }
      );

      // 3. Compose + send to the contractor's own address.
      const subject = buildSelfSubject();
      const linkLine = reviewLink
        ? `Click the link to view the proposal:<br/><a href="${reviewLink}">${reviewLink}</a>`
        : "(Proposal link unavailable — open the attached file in the app.)";
      const html =
        `<div style="font-family: system-ui, -apple-system, Segoe UI, Arial, sans-serif; font-size:14px; line-height:1.6; color:#111;">` +
        `<p>Your estimate file is attached; click the link to view the proposal.</p>` +
        `<p>${linkLine}</p>` +
        `</div>`;
      const text = reviewLink
        ? `Your estimate file is attached; click the link to view the proposal.\n\n${reviewLink}`
        : `Your estimate file is attached.`;

      const { error } = await invokeSendProposalEmail(supabase, {
        to: selfEmail,
        subject,
        html,
        proposalId: proposalId || undefined,
        replyTo: selfEmail,
        text,
        attachments: [{ filename, content: fileBase64 }],
      });

      if (error) {
        const msg = String(
          (error as any)?.message ||
            (error as any)?.context?.body ||
            "Send failed"
        );
        setEmailStatus({ ok: false, text: `Couldn’t email it: ${msg}` });
        return;
      }
      setEmailStatus({ ok: true, text: `Sent to ${selfEmail}` });
      // Mark this estimate exported so "New" won't warn until it's edited again.
      setExported(true);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn("[EstimatorMobile] email-to-self failed", err);
      setEmailStatus({
        ok: false,
        text: err?.message
          ? `Couldn’t email it: ${err.message}`
          : "Couldn’t email it.",
      });
    } finally {
      setEmailBusy(false);
    }
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  if (!bootstrapped) {
    return (
      <main className="m-content">
        <div className="m-placeholder">
          <div className="m-placeholder-icon">⟳</div>
          <div className="m-placeholder-title">Loading…</div>
          <div className="m-placeholder-sub">
            Restoring your draft from the cloud.
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      {/* ===== STICKY PRICING STRIP (Uplift + MSRP) =====
          Lives as a flex sibling between .m-page-header and .m-content
          so the breakdown panel can push the scroll area down instead
          of floating over it. */}
      <div className="m-pricing-strip" role="group" aria-label="Pricing controls">
        <button
          type="button"
          className={`m-pill m-pill--uplift${upliftOpen ? " is-open" : ""}`}
          onClick={() => setUpliftOpen((v) => !v)}
          aria-expanded={upliftOpen}
          aria-controls="m-uplift-panel"
          aria-label="Uplift"
        >
          <span className="m-pill-value">
            {formatMultiplier(estimate.upliftMultiplier)}
          </span>
        </button>
        <button
          type="button"
          className={`m-pill m-pill--msrp${
            snapshot.msrpMode ? " is-on" : ""
          }`}
          onClick={() => patchSnapshot({ msrpMode: !snapshot.msrpMode })}
          aria-pressed={snapshot.msrpMode}
        >
          <span className="m-pill-label">MSRP</span>
          <span className="m-pill-value">
            {snapshot.msrpMode ? "ON" : "OFF"}
          </span>
        </button>
      </div>

      {upliftOpen && (
        <div
          id="m-uplift-panel"
          className="m-uplift-panel"
          role="region"
          aria-label="Uplift breakdown"
        >
          <div className="m-uplift-row m-uplift-row--strong">
            <span>Base Price</span>
            <span>${money0(upliftBreakdown.basePrice)}</span>
          </div>
          <div className="m-uplift-row">
            <span>Permit</span>
            <span>
              {Math.round(estimate.permitPercent)}% ($
              {money0(upliftBreakdown.permitAmt)})
            </span>
          </div>
          <div className="m-uplift-row">
            <span>Small Job</span>
            <span>
              {Math.round(estimate.smallJobPercent)}% ($
              {money0(upliftBreakdown.smallJobAmt)})
            </span>
          </div>
          <div className="m-uplift-row m-uplift-row--strong">
            <span>MSRP</span>
            <span>${money0(upliftBreakdown.msrp)}</span>
          </div>
          <div className="m-uplift-row">
            <span>Perceived Value</span>
            <span>
              {Math.round(estimate.perceivedPercent)}% ($
              {money0(upliftBreakdown.perceivedAmt)})
            </span>
          </div>
          <div className="m-uplift-row">
            <span>Finance</span>
            <span>
              {Math.round(estimate.financePercent)}% ($
              {money0(upliftBreakdown.financeAmt)})
            </span>
          </div>
          <div className="m-uplift-row">
            <span>Manual Index</span>
            <span>
              {Math.round(estimate.miPercent)}% ($
              {money0(upliftBreakdown.miAmt)})
            </span>
          </div>
          <div className="m-uplift-row m-uplift-row--italic">
            <span>Sub-Total Uplift</span>
            <span>${money0(upliftBreakdown.upliftSubtotal)}</span>
          </div>
          <div className="m-uplift-row m-uplift-row--total">
            <span>Total Cost</span>
            <span>${money0(upliftBreakdown.totalCost)}</span>
          </div>
        </div>
      )}

      <main
        className="m-content"
        aria-label="New estimate"
        ref={contentRef}
        onKeyDown={handleContentKeyDown}
      >
        <CustomerCard
          value={customer}
          onChange={onCustomerChange}
          active={activeStep === "customer"}
          canImport={props.canImport ?? false}
          isOnline={props.isOnline ?? true}
        />

        {/* ----- Construction Type (wired) ----- */}
        <LineItemCard
          name="Construction Type"
          configured={constructionConfigured}
          active={activeStep === "construction"}
          onActivate={() => onCardTap("construction")}
        >
          <div className="m-field">
            <span className="m-section-label">Type</span>
            <CollapsingOptionPicker
              ariaLabel="Construction type"
              options={constructionOptions.map((opt) => ({
                // Snapshot stores the name (not the row id), so the
                // picker's `id` field must be the name too.
                id: opt.name,
                name: opt.name,
              }))}
              selectedId={snapshot.constructionType || ""}
              onSelect={(id) => patchSnapshot({ constructionType: id })}
            />
          </div>
        </LineItemCard>

        {/* ----- MI input + Permit toggle (compact row) -----
            Sits between Construction Type and Decking. Same fields
            desktop uses: snapshot.miValue and snapshot.includePermit. */}
        <div className="m-mini-row">
          <label className="m-mini-field" htmlFor="m-mi-input">
            <span className="m-mini-field-label">MI</span>
            <input
              id="m-mi-input"
              className="m-mini-input"
              type="number"
              inputMode="decimal"
              step="0.01"
              value={snapshot.miValue || ""}
              onChange={(e) =>
                patchSnapshot({ miValue: Number(e.target.value) || 0 })
              }
            />
          </label>
          <button
            type="button"
            className={`m-pill m-pill--permit${
              snapshot.includePermit ? " is-on" : ""
            }`}
            onClick={() =>
              patchSnapshot({ includePermit: !snapshot.includePermit })
            }
            aria-pressed={snapshot.includePermit}
          >
            <span className="m-pill-label">Permit</span>
            <span className="m-pill-value">
              {snapshot.includePermit ? "ON" : "OFF"}
            </span>
          </button>
        </div>

        {/* ----- DECKING (wired) ----- */}
        <LineItemCard
          name="Decking"
          configured={deckingConfigured}
          subtotal={estimate.deckingSubtotal}
          active={activeStep === "decking"}
          onActivate={() => onCardTap("decking")}
        >
          <div className="m-field">
            <span className="m-section-label">Type</span>
            <CollapsingOptionPicker
              ariaLabel="Decking product"
              options={deckingOptions.map((opt) => ({
                id: String(opt.id),
                name: opt.name,
              }))}
              selectedId={snapshot.selectedDeckingId || ""}
              onSelect={(id) => patchSnapshot({ selectedDeckingId: id })}
            />
          </div>
          <div className="m-field">
            <label className="m-section-label" htmlFor="m-decking-sf">
              Square feet
            </label>
            <input
              id="m-decking-sf"
              className="m-input"
              type="number"
              inputMode="decimal"
              min={0}
              value={snapshot.deckingSqFt || ""}
              onChange={(e) =>
                patchSnapshot({ deckingSqFt: Number(e.target.value) || 0 })
              }
            />
          </div>
        </LineItemCard>

        {/* ----- RAILING (wired) ----- */}
        <LineItemCard
          name="Railing"
          configured={railingConfigured}
          subtotal={estimate.railingSubtotal}
          active={activeStep === "railing"}
          onActivate={() => onCardTap("railing")}
        >
          <div className="m-field">
            <span className="m-section-label">Type</span>
            <CollapsingOptionPicker
              ariaLabel="Railing product"
              options={railingOptions.map((opt) => ({
                id: String(opt.id),
                name: opt.name,
              }))}
              selectedId={snapshot.selectedRailingId || ""}
              onSelect={(id) => patchSnapshot({ selectedRailingId: id })}
            />
          </div>
          <div className="m-field">
            <label className="m-section-label" htmlFor="m-railing-lf">
              Linear feet
            </label>
            <input
              id="m-railing-lf"
              className="m-input"
              type="number"
              inputMode="decimal"
              min={0}
              value={snapshot.railingLf || ""}
              onChange={(e) =>
                patchSnapshot({ railingLf: Number(e.target.value) || 0 })
              }
            />
          </div>
        </LineItemCard>

        {/* ----- STAIRS (wired) ----- */}
        <LineItemCard
          name="Stairs"
          configured={stairsConfigured}
          subtotal={estimate.stairsSubtotal}
          active={activeStep === "stairs"}
          onActivate={() => onCardTap("stairs")}
        >
          <div className="m-field">
            <span className="m-section-label">Stair option</span>
            <CollapsingOptionPicker
              ariaLabel="Stair option"
              options={mainStairsOptions.map((opt) => ({
                id: String(opt.id),
                name: opt.name,
              }))}
              selectedId={snapshot.selectedStairsId || ""}
              onSelect={(id) => patchSnapshot({ selectedStairsId: id })}
            />
          </div>
          <div className="m-field">
            <label className="m-section-label" htmlFor="m-stairs-lf">
              Linear feet of treads
            </label>
            <input
              id="m-stairs-lf"
              className="m-input"
              type="number"
              inputMode="decimal"
              min={0}
              value={snapshot.stairsCount || ""}
              onChange={(e) =>
                patchSnapshot({ stairsCount: Number(e.target.value) || 0 })
              }
            />
          </div>
        </LineItemCard>

        {/* ----- DEMO (wired) ----- */}
        <LineItemCard
          name="Demo"
          configured={demoConfigured}
          subtotal={estimate.demoSubtotal}
          active={activeStep === "demo"}
          onActivate={() => onCardTap("demo")}
        >
          <div className="m-field">
            <span className="m-section-label">Demo type</span>
            <CollapsingOptionPicker
              ariaLabel="Demo type"
              options={demoOptions.map((opt) => ({
                id: String(opt.id),
                name: opt.name,
              }))}
              selectedId={snapshot.selectedDemoId || ""}
              onSelect={(id) => patchSnapshot({ selectedDemoId: id })}
            />
          </div>
          <div className="m-field">
            <label className="m-section-label" htmlFor="m-demo-qty">
              Quantity ({estimate.selectedDemo?.unit || "ea"})
            </label>
            <input
              id="m-demo-qty"
              className="m-input"
              type="number"
              inputMode="decimal"
              min={0}
              value={snapshot.demoQty || ""}
              onChange={(e) =>
                patchSnapshot({ demoQty: Number(e.target.value) || 0 })
              }
            />
          </div>
        </LineItemCard>

        {/* ----- FASTENERS (wired) ----- */}
        <LineItemCard
          name="Fasteners"
          configured={fastenerConfigured}
          subtotal={estimate.fastenerSubtotal}
          active={activeStep === "fastener"}
          onActivate={() => onCardTap("fastener")}
        >
          <div className="m-field">
            <span className="m-section-label">Fastener type</span>
            <CollapsingOptionPicker
              ariaLabel="Fastener type"
              options={fastenerOptions.map((opt) => ({
                id: String(opt.id),
                name: opt.name,
              }))}
              selectedId={snapshot.selectedFastenerId || ""}
              onSelect={(id) =>
                patchSnapshot({ selectedFastenerId: id })
              }
            />
          </div>
          <div className="m-field">
            <label className="m-section-label" htmlFor="m-fastener-qty">
              Auto quantity (= Decking SF)
            </label>
            <input
              id="m-fastener-qty"
              className="m-input"
              type="number"
              inputMode="decimal"
              value={
                estimate.fastenerQtyAuto === 0 ? "" : estimate.fastenerQtyAuto
              }
              readOnly
            />
          </div>
        </LineItemCard>

        {/* ----- SKIRTING / LATTICE (wired) ----- */}
        <LineItemCard
          name="Skirt"
          configured={skirtConfigured}
          subtotal={estimate.skirtingSubtotal}
          active={activeStep === "skirt"}
          onActivate={() => onCardTap("skirt")}
        >
          <div className="m-field">
            <span className="m-section-label">Skirting / Lattice</span>
            <CollapsingOptionPicker
              ariaLabel="Skirt category"
              options={[
                { id: "Skirting", name: "Skirting" },
                { id: "Lattice", name: "Lattice" },
              ]}
              selectedId={snapshot.skirtingCategory || ""}
              onSelect={(id) => {
                // User-driven change — stop the decking-driven prefill
                // from overwriting future selections this session.
                skirtingUserTouchedRef.current = true;
                patchSnapshot({
                  skirtingCategory: id,
                  // reset type selection when category changes (mirrors desktop)
                  selectedSkirtingId: "",
                });
              }}
            />
          </div>
          {snapshot.skirtingCategory && (
            <div className="m-field">
              <span className="m-section-label">Type</span>
              <CollapsingOptionPicker
                ariaLabel="Skirt type"
                options={estimate.skirtingLatticeOptions.map((opt) => ({
                  id: String(opt.id),
                  name: opt.name,
                }))}
                selectedId={snapshot.selectedSkirtingId || ""}
                onSelect={(id) => {
                  skirtingUserTouchedRef.current = true;
                  patchSnapshot({ selectedSkirtingId: id });
                }}
              />
            </div>
          )}
          <div className="m-field">
            <label className="m-section-label" htmlFor="m-skirt-sf">
              Square feet
            </label>
            <input
              id="m-skirt-sf"
              className="m-input"
              type="number"
              inputMode="decimal"
              min={0}
              value={snapshot.skirtingSf || ""}
              onChange={(e) =>
                patchSnapshot({ skirtingSf: Number(e.target.value) || 0 })
              }
            />
          </div>
        </LineItemCard>

        {/* ----- Placeholders (R2c will wire) ----- */}
        {PLACEHOLDER_AFTER.map((name) => (
          <LineItemCard key={name} name={name} />
        ))}

        {/* ----- Add Items (user-added line items, mirrors desktop's
            addItems array shape so cross-device sync round-trips). ----- */}
        {addItems.map((row, idx) => {
          const detailed = estimate.addItemsDetailed[idx];
          const cat = normalizeCat(row.category || "");
          const isConstruction = isConstructionTypeCategory(row.category || "");
          const isMisc = cat === "misc";
          const isBench = cat === "bench" && !isConstruction;
          const isDefault = !isConstruction && !isMisc && !isBench;

          const typeOptions = isDefault ? addItemOptionsForRow(pricingItems, row) : [];
          const subtotal = Number(detailed?.lineBase) || 0;
          const titleParts = [
            detailed?.displayName?.trim() || (row.category ? row.category.replace(/_/g, " ") : "New item"),
          ];

          // Merged category options: standard add-item categories first,
          // then Construction Type variants. Both write into row.category;
          // the renderer routes by isConstructionTypeCategory().
          //
          // A SAVED row may carry a category that is no longer offered
          // ("Construction Options" is main-section-only and is filtered out
          // of the Add Item list). Re-add it for THIS row only, so a legacy
          // row still displays what was saved; it is absent from every other
          // row and vanishes as soon as this row's category is changed.
          const rowCat = String(row.category || "").trim();
          const showLegacyCatOption =
            !!rowCat &&
            !isConstruction &&
            !addItemCategories.some(
              (c) => normalizeCat(c) === normalizeCat(rowCat)
            );

          const categoryPickerOptions = [
            ...addItemCategories.map((c) => ({
              id: c,
              name: c.replace(/_/g, " "),
            })),
            ...(showLegacyCatOption
              ? [{ id: rowCat, name: rowCat.replace(/_/g, " ") }]
              : []),
            ...CONSTRUCTION_TYPES.filter((t) => !!t.value).map((t) => ({
              id: t.value,
              name: t.label,
            })),
          ];

          return (
            <section
              key={row.rowId}
              className={`m-additem-card${row.optional ? " is-optional" : ""}`}
              aria-label={`Add item ${idx + 1}`}
            >
              <header className="m-additem-head">
                <div className="m-additem-title">
                  <span className="m-line-name">{titleParts.join(" — ")}</span>
                  {subtotal > 0 && (
                    <span className="m-line-amount">
                      {subtotal.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  )}
                </div>
                <label
                  className="m-additem-optional-toggle"
                  title="Price out but EXCLUDE from main estimate total. Shows under Optional Add-Ons on the proposal."
                >
                  <input
                    type="checkbox"
                    checked={!!row.optional}
                    onChange={(e) =>
                      updateAddItemRow(row.rowId, {
                        optional: e.target.checked,
                      })
                    }
                  />
                  <span>Opt</span>
                </label>
                <button
                  type="button"
                  className="m-additem-remove"
                  onClick={() => removeAddItemRow(row.rowId)}
                  aria-label="Remove item"
                  title="Remove"
                >
                  ×
                </button>
              </header>

              <div className="m-additem-body">
                {/* Category — always present */}
                <div className="m-field">
                  <span className="m-section-label">Category</span>
                  <CollapsingOptionPicker
                    ariaLabel="Add item category"
                    options={categoryPickerOptions}
                    selectedId={row.category || ""}
                    onSelect={(nextCat) => {
                      updateAddItemRow(row.rowId, {
                        category: nextCat,
                        itemId: "",
                        qty: 0,
                        customName: "",
                        customPrice: 0,
                        customQtyText: "",
                        customDescription: "",
                        constructionType: "",
                        deckingId: "",
                        benchType:
                          normalizeCat(nextCat) === "bench" ? "12_flat" : "",
                      });
                    }}
                  />
                </div>

                {/* Branch 1: Construction Type add-item */}
                {isConstruction && (
                  <>
                    <div className="m-field">
                      <span className="m-section-label">Decking type</span>
                      <CollapsingOptionPicker
                        ariaLabel="Add item decking type"
                        options={deckingOptions.map((opt) => ({
                          id: String(opt.id),
                          name: opt.name,
                        }))}
                        selectedId={row.deckingId || ""}
                        onSelect={(id) =>
                          updateAddItemRow(row.rowId, { deckingId: id })
                        }
                      />
                    </div>
                    <div className="m-field">
                      <label
                        className="m-section-label"
                        htmlFor={`m-ai-qty-${row.rowId}`}
                      >
                        Square feet
                      </label>
                      <input
                        id={`m-ai-qty-${row.rowId}`}
                        className="m-input"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        value={row.qty || ""}
                        onChange={(e) =>
                          updateAddItemRow(row.rowId, {
                            qty: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  </>
                )}

                {/* Branch 2: Misc — fixed-price free-form line item */}
                {isMisc && (
                  <>
                    <div className="m-field">
                      <label
                        className="m-section-label"
                        htmlFor={`m-ai-name-${row.rowId}`}
                      >
                        Title
                      </label>
                      <input
                        id={`m-ai-name-${row.rowId}`}
                        className="m-input"
                        type="text"
                        placeholder="e.g. Screened Sunroom"
                        value={row.customName || ""}
                        onChange={(e) =>
                          updateAddItemRow(row.rowId, {
                            customName: e.target.value,
                            qty: 1,
                          })
                        }
                      />
                    </div>
                    <div className="m-field">
                      <label
                        className="m-section-label"
                        htmlFor={`m-ai-price-${row.rowId}`}
                      >
                        Price ($)
                      </label>
                      <input
                        id={`m-ai-price-${row.rowId}`}
                        className="m-input"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        value={row.customPrice || ""}
                        onChange={(e) =>
                          updateAddItemRow(row.rowId, {
                            customPrice: Number(e.target.value) || 0,
                            qty: 1,
                          })
                        }
                      />
                    </div>
                    <div className="m-field">
                      <label
                        className="m-section-label"
                        htmlFor={`m-ai-qtytext-${row.rowId}`}
                      >
                        Quantity (text)
                      </label>
                      <input
                        id={`m-ai-qtytext-${row.rowId}`}
                        className="m-input"
                        type="text"
                        placeholder="e.g. 400 sf"
                        value={row.customQtyText || ""}
                        onChange={(e) =>
                          updateAddItemRow(row.rowId, {
                            customQtyText: e.target.value,
                            qty: 1,
                          })
                        }
                      />
                    </div>
                    <div className="m-field">
                      <label
                        className="m-section-label"
                        htmlFor={`m-ai-desc-${row.rowId}`}
                      >
                        Description (optional)
                      </label>
                      <input
                        id={`m-ai-desc-${row.rowId}`}
                        className="m-input"
                        type="text"
                        value={row.customDescription || ""}
                        onChange={(e) =>
                          updateAddItemRow(row.rowId, {
                            customDescription: e.target.value,
                            qty: 1,
                          })
                        }
                      />
                    </div>
                  </>
                )}

                {/* Branch 3: Bench — bench-type dropdown + LF */}
                {isBench && (
                  <>
                    <div className="m-field">
                      <span className="m-section-label">Bench type</span>
                      <CollapsingOptionPicker
                        ariaLabel="Bench type"
                        options={BENCH_TYPES.map((bt) => ({
                          id: bt.value,
                          name: bt.label,
                        }))}
                        selectedId={row.benchType || "12_flat"}
                        onSelect={(id) =>
                          updateAddItemRow(row.rowId, { benchType: id })
                        }
                      />
                    </div>
                    <div className="m-field">
                      <label
                        className="m-section-label"
                        htmlFor={`m-ai-qty-${row.rowId}`}
                      >
                        Linear feet
                      </label>
                      <input
                        id={`m-ai-qty-${row.rowId}`}
                        className="m-input"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        value={row.qty || ""}
                        onChange={(e) =>
                          updateAddItemRow(row.rowId, {
                            qty: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  </>
                )}

                {/* Branch 4: Default — type dropdown + qty */}
                {isDefault && row.category && (
                  <>
                    <div className="m-field">
                      <span className="m-section-label">Type</span>
                      <CollapsingOptionPicker
                        ariaLabel="Add item type"
                        options={typeOptions.map((opt) => ({
                          id: String(opt.id),
                          name: opt.name,
                        }))}
                        selectedId={row.itemId || ""}
                        onSelect={(id) =>
                          updateAddItemRow(row.rowId, { itemId: id })
                        }
                      />
                    </div>
                    <div className="m-field">
                      <label
                        className="m-section-label"
                        htmlFor={`m-ai-qty-${row.rowId}`}
                      >
                        Quantity{detailed?.unit ? ` (${detailed.unit})` : ""}
                      </label>
                      <input
                        id={`m-ai-qty-${row.rowId}`}
                        className="m-input"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        value={row.qty || ""}
                        onChange={(e) =>
                          updateAddItemRow(row.rowId, {
                            qty: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </section>
          );
        })}

        <button
          type="button"
          className="m-add-item-btn"
          onClick={addAddItemRow}
        >
          + Add Item
        </button>
      </main>

      <MissingFieldsSheet
        missing={missingSections}
        onGoBack={() => setMissingSections(null)}
        onContinue={acknowledgeMissing}
      />

      <StickyTotalBar
        total={estimate.finalEstimate}
        canSubmit={canSubmit}
        onReview={onReview}
        canEmail={canEmailSelf}
        onEmail={onEmailSelf}
        emailBusy={emailBusy}
        emailHint={
          !emailStatus && hasEstimate && dropboxEnabled
            ? dropboxFile
              ? `Saves back to ${dropboxFile.split("/").filter(Boolean).slice(-2).join(" › ")}`
              : "Saves the estimate file to your Dropbox"
            : !emailStatus && hasEstimate && selfEmail && dropboxConnectable
              ? "Tip: connect Dropbox in Settings for one-tap saves"
              : emailHint
        }
        emailOk={emailStatus?.ok ?? null}
        onDropbox={dropboxEnabled ? onSaveDropbox : undefined}
        canDropbox={hasEstimate && !dropboxBusy && !emailBusy}
        dropboxBusy={dropboxBusy}
      />
    </>
  );
}

/* -------------------------------------------------------------------
   MISSING-FIELDS SHEET
   -------------------------------------------------------------------
   Bottom sheet listing the blank / half-filled scope categories on the
   way to Review & Send. Not a blocker — plenty of jobs have no stairs
   or no demo — but it has to be read and dismissed on purpose.
   ------------------------------------------------------------------- */
function MissingFieldsSheet({
  missing,
  onGoBack,
  onContinue,
}: {
  missing: MissingSection[] | null;
  onGoBack: () => void;
  onContinue: () => void;
}) {
  if (!missing || missing.length === 0) return null;

  const partials = missing.filter((m) => m.kind === "partial").length;

  return (
    <div
      className="m-send-backdrop"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="m-missing-title"
      onClick={onGoBack}
    >
      <div className="m-send-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="m-send-grabber" aria-hidden />

        <header className="m-send-head">
          <h2 className="m-send-title" id="m-missing-title">
            {missing.length === 1
              ? "1 thing to check"
              : `${missing.length} things to check`}
          </h2>
        </header>

        <div className="m-send-body">
          <p className="m-missing-lede">
            {partials > 0
              ? "Check these before the proposal goes out — a half-filled category prices as $0."
              : "Check these before the proposal goes out. If the job really doesn't include them, continue."}
          </p>

          <ul className="m-missing-list">
            {missing.map((m) => (
              <li
                key={m.key}
                className={
                  m.kind === "partial"
                    ? "m-missing-item m-missing-item--partial"
                    : "m-missing-item"
                }
              >
                <span className="m-missing-label">{m.label}</span>
                <span className="m-missing-detail">{m.detail}</span>
              </li>
            ))}
          </ul>
        </div>

        <footer className="m-send-foot">
          <button type="button" className="m-send-cancel" onClick={onContinue}>
            Continue anyway
          </button>
          <button type="button" className="m-send-primary" onClick={onGoBack}>
            Go back and fix
          </button>
        </footer>
      </div>
    </div>
  );
}
